import { useState, useContext } from "react";
import { useSnackbar } from "notistack";
import { context } from "utils/Provider";
import { useRemoveMatcherViewMutation } from "../../../../graphql/types/graphql";

interface UseRemoveMatcherViewsReturn {
  removeMatcherView: (viewName: string) => Promise<void>;
  removeMatcherViewLoading: boolean;
  dialogOpen: string | null;
  handleOpenDialog: (viewName: string) => void;
  handleCloseDialog: () => void;
  handleConfirmDelete: (viewName: string) => Promise<void>;
}

export const useRemoveMatcherViews = (): UseRemoveMatcherViewsReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const { setIsLoading } = useContext(context);
  const [dialogOpen, setDialogOpen] = useState<string | null>(null);

  const [removeMatcherView, { loading: removeMatcherViewLoading }] = useRemoveMatcherViewMutation({
    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "matcherTables",
      });
    },
  });

  const handleOpenDialog = (viewName: string) => {
    setDialogOpen(viewName);
  };

  const handleCloseDialog = () => {
    setDialogOpen(null);
  };

  const handleConfirmDelete = async (viewName: string) => {
    setIsLoading(true);
    try {
      const response = await removeMatcherView({
        variables: { viewName },
      });
      if (response.data?.removeMatcherView?.success) {
        enqueueSnackbar(`View ${viewName} deleted successfully!`, { variant: "success" });
      } else {
        enqueueSnackbar(response.data?.removeMatcherView?.message || "Failed to delete view", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(`Delete error: ${error instanceof Error ? error.message : String(error)}`, { variant: "error" });
    } finally {
      setIsLoading(false);
      handleCloseDialog();
    }
  };

  return {
    removeMatcherView: async (viewName: string) => {
      await handleConfirmDelete(viewName);
    },
    removeMatcherViewLoading,
    dialogOpen,
    handleOpenDialog,
    handleCloseDialog,
    handleConfirmDelete,
  };
};
